import {
  faCircle,
  faSquare,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { useParams } from 'react-router';

import { PeriodontalFindingsIcon } from '@/svgs/imageToolbar/periodontalFindingsIcon';
import { ToothAnatomyIcon } from '@/svgs/imageToolbar/toothAnatomyIcon';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import type { ModelsToggles } from '../findingSidebar/atoms';
import {
  modelsTogglesAtom,
  toothPartsTogglesAtom,
} from '../findingSidebar/atoms';
import { isExpandedAtom } from '../multiFileViewer/imageToolbar/atoms';
import type { ImageObject } from '../types';
import { useGetModelFeatureFlags } from '../utils';

import { DisplayThresholdToggle } from './displayThresholdToggle';
import { ImageControls } from './imageControls';

interface CustomToggleGroupItemProps {
  value: string;
  isEnabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
  tooltip: string;
}

const CustomToggleGroupItem = ({
  value,
  isEnabled,
  onClick,
  children,
  tooltip,
}: CustomToggleGroupItemProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <ToggleGroupItem
        value={value}
        data-testid={`${value}-toggle`}
        onClick={onClick}
        className={classNames(
          'vai-rounded-sm !vai-p-0 !vai-min-w-0 !vai-min-h-0',
          'max-[880px]:!vai-w-7 max-[880px]:!vai-h-7',
          'min-[880px]:!vai-w-9 min-[880px]:!vai-h-9',
          {
            'active !vai-bg-[#0A0A0A] hover:!vai-opacity-70': isEnabled,
            '!vai-bg-transparent hover:!vai-bg-[#212734]': !isEnabled,
          },
        )}
      >
        {children}
      </ToggleGroupItem>
    </TooltipTrigger>
    <TooltipContent variant="dark" side="right" sideOffset={8} alignOffset={-4}>
      <p>{tooltip}</p>
    </TooltipContent>
  </Tooltip>
);

interface IndividualFindingsTogglesProps {
  enabledFindingTypes: ModelsToggles;
  setEnabledFindingTypes: (modelsToggles: ModelsToggles) => void;
  toothPartsToggles: {
    tooth_anatomy: boolean;
  };
  setToothPartsToggles: (toggles: { tooth_anatomy: boolean }) => void;
  patientId: string;
  direction?: 'row' | 'col';
}

const IndividualFindingsToggles = ({
  enabledFindingTypes,
  setEnabledFindingTypes,
  toothPartsToggles,
  setToothPartsToggles,
  patientId,
  direction = 'col',
}: IndividualFindingsTogglesProps) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();

  return (
    <div
      className={classNames(
        'vai-flex vai-gap-1',
        direction === 'row' ? 'vai-flex-row' : 'vai-flex-col',
      )}
    >
      <CustomToggleGroupItem
        value="caries"
        isEnabled={enabledFindingTypes.caries}
        tooltip={t`Restorative`}
        onClick={() => {
          const newFindingsTypes = {
            ...enabledFindingTypes,
            caries: !enabledFindingTypes.caries,
            restorative_adjacent_lucency: !enabledFindingTypes.caries,
          };
          setEnabledFindingTypes(newFindingsTypes);
          trackEvent(mixpanelEvents.toggleFindings, {
            toggled: {
              category: 'restorative',
              value: !enabledFindingTypes.caries,
            },
            previousFindingsTypes: enabledFindingTypes,
            newFindingsTypes: newFindingsTypes,
            patientId,
          });
        }}
      >
        <FontAwesomeIcon
          icon={faSquare}
          className="vai-text-[#DD174C] max-[880px]:!vai-h-4 max-[880px]:!vai-w-4 min-[880px]:!vai-h-5 min-[880px]:!vai-w-5"
        />
      </CustomToggleGroupItem>

      <CustomToggleGroupItem
        value="incipients"
        isEnabled={enabledFindingTypes.incipients}
        tooltip={t`Incipient`}
        onClick={() => {
          const newFindingsTypes = {
            ...enabledFindingTypes,
            incipients: !enabledFindingTypes.incipients,
          };
          setEnabledFindingTypes(newFindingsTypes);
          trackEvent(mixpanelEvents.toggleFindings, {
            toggled: {
              category: 'incipients',
              value: !enabledFindingTypes.incipients,
            },
            previousFindingsTypes: enabledFindingTypes,
            newFindingsTypes: newFindingsTypes,
            patientId,
          });
        }}
      >
        <FontAwesomeIcon
          icon={faSquare}
          className="vai-text-[#D4A700] max-[880px]:!vai-h-2.5 max-[880px]:!vai-w-2.5 min-[880px]:!vai-h-3 min-[880px]:!vai-w-3"
        />
      </CustomToggleGroupItem>

      <CustomToggleGroupItem
        value="perio"
        isEnabled={enabledFindingTypes.radiographic_boneloss}
        tooltip={t`Periodontal`}
        onClick={() => {
          const newFindingsTypes = {
            ...enabledFindingTypes,
            radiographic_boneloss: !enabledFindingTypes.radiographic_boneloss,
            calculus: !enabledFindingTypes.radiographic_boneloss,
          };
          setEnabledFindingTypes(newFindingsTypes);
          trackEvent(mixpanelEvents.toggleFindings, {
            toggled: {
              category: 'radiographic_boneloss',
              value: !enabledFindingTypes.radiographic_boneloss,
            },
            previousFindingsTypes: enabledFindingTypes,
            newFindingsTypes: newFindingsTypes,
            patientId,
          });
        }}
      >
        <PeriodontalFindingsIcon className="max-[880px]:!vai-w-4 max-[880px]:!vai-h-4 min-[880px]:!vai-w-5 min-[880px]:!vai-h-5" />
      </CustomToggleGroupItem>

      <CustomToggleGroupItem
        value="prl"
        isEnabled={enabledFindingTypes.periapical_radiolucency}
        tooltip={t`Endodontic`}
        onClick={() => {
          const newFindingsTypes = {
            ...enabledFindingTypes,
            periapical_radiolucency:
              !enabledFindingTypes.periapical_radiolucency,
          };
          setEnabledFindingTypes(newFindingsTypes);
          trackEvent(mixpanelEvents.toggleFindings, {
            toggled: {
              category: 'periapical_radiolucency',
              value: !enabledFindingTypes.periapical_radiolucency,
            },
            previousFindingsTypes: enabledFindingTypes,
            newFindingsTypes: newFindingsTypes,
            patientId,
          });
        }}
      >
        <FontAwesomeIcon
          icon={faCircle}
          className="vai-text-[#992D5B] max-[880px]:!vai-h-4 max-[880px]:!vai-w-4 min-[880px]:!vai-h-5 min-[880px]:!vai-w-5"
        />
      </CustomToggleGroupItem>

      <CustomToggleGroupItem
        value="anatomy"
        isEnabled={toothPartsToggles.tooth_anatomy}
        tooltip={t`Tooth Anatomy`}
        onClick={() => {
          setToothPartsToggles({
            ...toothPartsToggles,
            tooth_anatomy: !toothPartsToggles.tooth_anatomy,
          });
          trackEvent(mixpanelEvents.toggleFindings, {
            toggled: {
              category: 'tooth_anatomy',
              value: !toothPartsToggles.tooth_anatomy,
            },
          });
        }}
      >
        <ToothAnatomyIcon className="max-[880px]:!vai-w-4 max-[880px]:!vai-h-4 min-[880px]:!vai-w-5 min-[880px]:!vai-h-5" />
      </CustomToggleGroupItem>
    </div>
  );
};

export const FindingsToggles = ({
  activeImage,
  setShouldPersistUserModifications,
  onChangeViewerMode,
}: {
  activeImage?: ImageObject;
  setShouldPersistUserModifications?: (value: boolean) => void;
  onChangeViewerMode?: (mode: number) => void;
}) => {
  const [enabledFindingTypes, setEnabledFindingTypes] =
    useAtom(modelsTogglesAtom);
  const [toothPartsToggles, setToothPartsToggles] = useAtom(
    toothPartsTogglesAtom,
  );
  const isExpanded = useAtomValue(isExpandedAtom);
  const { isCalculusEnabled, isAnatomyEnabled, isRblEnabled, isPrlEnabled } =
    useGetModelFeatureFlags();

  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const { id: patientId, imageId } = useParams<{
    id: string;
    imageId: string;
  }>();

  const isAllFindingsEnabled =
    (!isCalculusEnabled || enabledFindingTypes.calculus) &&
    (!isRblEnabled || enabledFindingTypes.radiographic_boneloss) &&
    (!isPrlEnabled || enabledFindingTypes.periapical_radiolucency) &&
    (!isAnatomyEnabled || toothPartsToggles.tooth_anatomy) &&
    enabledFindingTypes.caries &&
    enabledFindingTypes.incipients &&
    enabledFindingTypes.radiographic_boneloss &&
    enabledFindingTypes.restorative_adjacent_lucency;

  const handleToggleAllFindings = () => {
    if (isAllFindingsEnabled) {
      const newFindingsTypes = {
        area_of_concern: false,
        calculus: false,
        caries: false,
        incipients: false,
        periapical_radiolucency: false,
        radiographic_boneloss: false,
        restorative_adjacent_lucency: false,
        user: false,
      };
      trackEvent(mixpanelEvents.toggleFindings, {
        toggled: {
          category: 'all',
          value: !isAllFindingsEnabled,
        },
        previousFindingsTypes: enabledFindingTypes,
        newFindingsTypes: newFindingsTypes,
        patientId,
      });
      setEnabledFindingTypes(newFindingsTypes);
      setToothPartsToggles({
        ...toothPartsToggles,
        tooth_anatomy: false,
      });
    } else {
      const newFindingsTypes = {
        area_of_concern: true,
        calculus: true,
        caries: true,
        incipients: true,
        periapical_radiolucency: true,
        radiographic_boneloss: true,
        restorative_adjacent_lucency: true,
        user: true,
      };
      trackEvent(mixpanelEvents.toggleFindings, {
        toggled: {
          category: 'all',
          value: !isAllFindingsEnabled,
        },
        previousFindingsTypes: enabledFindingTypes,
        newFindingsTypes: newFindingsTypes,
        patientId,
      });
      setEnabledFindingTypes(newFindingsTypes);
      setToothPartsToggles({
        ...toothPartsToggles,
        tooth_anatomy: true,
      });
    }
  };

  return (
    <ToggleGroup
      type="multiple"
      className="vai-flex vai-flex-col vai-bg-[#393E4D] vai-rounded-sm vai-p-1"
    >
      <CustomToggleGroupItem
        value="all"
        isEnabled={isAllFindingsEnabled}
        tooltip={isAllFindingsEnabled ? t`Toggle Off AI` : t`Toggle On AI`}
        onClick={handleToggleAllFindings}
      >
        <span className="vai-text-aqua-500 max-[880px]:vai-text-base min-[880px]:vai-text-xl vai-font-semibold">
          <Trans>AI</Trans>
        </span>
      </CustomToggleGroupItem>

      <IndividualFindingsToggles
        enabledFindingTypes={enabledFindingTypes}
        setEnabledFindingTypes={setEnabledFindingTypes}
        toothPartsToggles={toothPartsToggles}
        setToothPartsToggles={setToothPartsToggles}
        patientId={patientId}
      />

      <DisplayThresholdToggle />
      {activeImage &&
        setShouldPersistUserModifications &&
        onChangeViewerMode && (
          <ImageControls
            activeImage={activeImage}
            setShouldPersistUserModifications={
              setShouldPersistUserModifications
            }
            onChangeViewerMode={onChangeViewerMode}
          />
        )}
    </ToggleGroup>
  );
};
